import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Send, Bell, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` } });

export default function AdminManualSend() {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState("");
  const [testNumber, setTestNumber] = useState("");
  const [testTemplate, setTestTemplate] = useState("registration_confirmation");
  const [sending, setSending] = useState("");

  useEffect(() => {
    axios.get(`${API}/admin/events`, authHeaders()).then(r => setEvents(r.data)).catch(() => {});
  }, []);

  const bulkReminder = async () => {
    if (!selectedEvent) return toast.error("Select an event");
    setSending("reminder");
    try {
      const r = await axios.post(`${API}/admin/bulk-send/event-reminder/${selectedEvent}`, {}, authHeaders());
      toast.success(`Sent: ${r.data.sent}, Failed: ${r.data.failed}`);
    } catch { toast.error("Failed"); }
    setSending("");
  };

  const bulkCheckin = async () => {
    if (!selectedEvent) return toast.error("Select an event");
    setSending("checkin");
    try {
      const r = await axios.post(`${API}/admin/bulk-send/checkin-details/${selectedEvent}`, {}, authHeaders());
      toast.success(`Sent: ${r.data.sent}, Failed: ${r.data.failed}`);
    } catch { toast.error("Failed"); }
    setSending("");
  };

  const testSend = async () => {
    if (!testNumber) return toast.error("Enter a phone number");
    setSending("test");
    try {
      const r = await axios.post(`${API}/admin/test-whatsapp`, {
        recipientNumber: testNumber, templateType: testTemplate, variables: { "1": "Admin", "2": "Test Event", "3": "2026-01-01", "4": "7:00 PM", "5": "Koregaon Park", "6": "Confirmed" }
      }, authHeaders());
      r.data.success ? toast.success("Test message sent!") : toast.error(r.data.error || "Failed");
    } catch { toast.error("Failed"); }
    setSending("");
  };

  return (
    <div data-testid="admin-manual-send">
      <h1 className="font-heading text-2xl tracking-tight font-medium mb-6">Manual Send Tools</h1>

      {/* Bulk Send */}
      <div className="bg-card border border-border/40 rounded-sm p-6 mb-6">
        <h2 className="font-heading text-lg tracking-tight font-medium mb-4">Bulk Send to Event</h2>
        <select value={selectedEvent} onChange={(e) => setSelectedEvent(e.target.value)} className="w-full h-9 rounded-sm border border-input bg-transparent px-3 text-sm mb-4" data-testid="admin-bulk-event-select">
          <option value="">Select event...</option>
          {events.map(ev => <option key={ev.id} value={ev.id}>{ev.title} — {ev.eventDate}</option>)}
        </select>
        <div className="flex gap-3">
          <Button onClick={bulkReminder} disabled={!!sending} className="rounded-sm gap-2 flex-1" variant="outline" data-testid="admin-bulk-reminder-btn">
            <Bell className="w-4 h-4" /> {sending === "reminder" ? "Sending..." : "Send Event Reminder"}
          </Button>
          <Button onClick={bulkCheckin} disabled={!!sending} className="rounded-sm gap-2 flex-1" variant="outline" data-testid="admin-bulk-checkin-btn">
            <MapPin className="w-4 h-4" /> {sending === "checkin" ? "Sending..." : "Send Check-in Details"}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-3">Sends to all confirmed/reserved bookings + confirmed waitlist who opted in to WhatsApp.</p>
      </div>

      {/* Test Send */}
      <div className="bg-card border border-border/40 rounded-sm p-6">
        <h2 className="font-heading text-lg tracking-tight font-medium mb-4">Test WhatsApp</h2>
        <div className="space-y-3">
          <Input value={testNumber} onChange={(e) => setTestNumber(e.target.value)} placeholder="+91 9876543210" className="rounded-sm" data-testid="admin-test-number-input" />
          <select value={testTemplate} onChange={(e) => setTestTemplate(e.target.value)} className="w-full h-9 rounded-sm border border-input bg-transparent px-3 text-sm" data-testid="admin-test-template-select">
            <option value="registration_confirmation">Registration Confirmation</option>
            <option value="event_reminder">Event Reminder</option>
            <option value="checkin_details">Check-in Details</option>
          </select>
          <Button onClick={testSend} disabled={!!sending} className="rounded-sm gap-2 w-full" data-testid="admin-test-send-btn">
            <Send className="w-4 h-4" /> {sending === "test" ? "Sending..." : "Send Test WhatsApp"}
          </Button>
        </div>
      </div>
    </div>
  );
}
